import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Tooltip } from "@mui/material";
import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { useEffect, useRef, useState } from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import PageLayout from "../../../../../components/PageLayout";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../components/app/DeleteModal";
import { BILLING_TABLE_ROW_HEIGHT } from "../../../../../components/app/provider/patient/chart";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useArchiveClaims } from "../../../../../queries/billings/claims/use-patient-claims-archive";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  formatClaimType,
  getCommaSeparatedValues,
  getConfigObject,
  getErrorSnackbar,
  // getFormattedId,
  getSuccessSnackbar,
  getValuesFromArrayOfObjects,
  getXtenantId,
  // hasActiveFilters,
  replaceUnderScoreAddSpace,
  roleFilter,
  toTwoDecimals,
} from "../../../../../utils/utils";
import {
  CLAIM_NOT_COMPLETELY_POSTED_MSG,
  CLAIM_POSTING_STATUS_PARTIALLY_PAID,
} from "@/pages/app/provider/billings/billing-constant";
import PrintSuperBill from "../PrintSuperBill";
import ClaimStatusCards from "./ClaimStatusCards";
import ClearingHouseResponseModal from "./ClearingHouseResponseModal";
// import IconButton from "@mui/material/IconButton";
// import ClearIcon from "@mui/icons-material/Clear";
import { useQueryClient } from "@tanstack/react-query";
import Flex from "../../../../../components/Flex";
import StatusChip from "../../../../../components/StatusChip";
import { useRoleController } from "../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAddElectronicClaim } from "../../../../../queries/billings/claims/use-add-electronic-claim";
import { useConfiguredClearinghouses } from "../../../../../queries/billings/claim-integration/use-configured-clearinghouses";
import { useGetClaimDenial } from "../../../../../queries/billings/claims/use-get-claim-denial";
// Superseded by the claim-by-id fetch inside ClaimEraDetailsTable.
// import { useGetClaimLite } from "../../../../../queries/billings/claims/use-get-claim-lite";
import { useGetClaimCount } from "../../../../../queries/billings/claims/use-get-claims-count";
import { useGetClaimsV2 } from "../../../../../queries/billings/claims/use-get-claims-v2";
import { useUpdateClaimStatus } from "../../../../../queries/billings/claims/use-update-claim-status";
import {
  ClaimControllerApi,
  ClaimRequest,
  ClaimRequestVendorTypeEnum,
} from "../../../../../services/api";
import SendBulkEmailModal from "../../communications/email/SendBulkEmailModal";
import UploadFaxDocumentDrawer from "../../communications/fax/UploadFaxDocumentDrawer";
import { DOCUMENT_INITIAL_VALUE } from "../../communications/fax/fax-document-constant";
import { ClaimFilterData } from "../BillingsLayout";
import { PLACES_OF_SERVICE_MAPPER } from "../add-super-bill-components/places-of-service";
import ChangeClaimStatus from "./ChangeClaimStatus";
import ClaimEraDetailsTable from "./ClaimEraDetailsTable";
import ViewDenialModal from "./ViewDenialModal";
import ViewPaperClaimDrawer from "./ViewPaperClaimDrawer";
// Superseded by the ERA procedure breakdown rendered in the accordion detail
// row (see ClaimEraDetailsTable). Kept for reference.
// import { buildCptChildRows, mapLiteToCptRows } from "./claim-cpt-breakdown";
import { CLAIMS_STATUS_LIST, CLAIM_TYPE, ClaimType } from "./claims-constants";
import NavSelectClaimTypeModal from "./updateClaims/NavSelectClaimTypeModal";

type OutletContextType = {
  filter: ClaimFilterData;
  setFilter: (v: ClaimFilterData) => void;
  setRowSelectionModel: (v: GridRowSelectionModel) => void;
  rowSelectionModel: GridRowSelectionModel;
  selectedClaimCard: string | undefined;
  setSelectedClaimCard: (v: string | undefined) => void;
  isBulkClaimActionAllowed: boolean;
  isReadyForSubmitView: boolean;
  paperClaimFilter: boolean;
};

const ClaimsList = () => {
  const [, setSearchParams] = useSearchParams();
  const {
    filter,
    // setFilter,
    setRowSelectionModel,
    rowSelectionModel,
    selectedClaimCard: selectedCard,
    setSelectedClaimCard: setSelectedCard,
    isBulkClaimActionAllowed,
    isReadyForSubmitView,
    paperClaimFilter,
  } = useOutletContext<OutletContextType>();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  // console.log("filter ", filter);
  const navigate = useNavigate();
  const { mutateAsync: archieveAPI } = useArchiveClaims();

  const [searchKey] = useDebounce<string>("");

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [selectedSuperBillUuid, setSelectedSuperBillUuid] = useState<
    string | null
  >(null);
  const {
    opened: openedDeleteModal,
    open: openDeleteModal,
    close: closeDeleteModal,
    deleteId,
  } = useDeleteDisClosure(false);

  const navSelectClaimTypeDisc = useDisclosure(false);

  const waystarResponseDisc = useDisclosure();

  const viewPaperClaimDisc = useDisclosure();

  const createFaxClaimDisc = useDisclosure();

  const changeClaimStatusDisclosureHost = useDisclosure();

  const { mutateAsync: addElectronicClaim } = useAddElectronicClaim();
  // When only one clearinghouse is configured, submit directly to it and skip
  // the vendor-selection screen.
  const { singleVendor } = useConfiguredClearinghouses();
  const sendMailDisclosure = useDisclosure();
  function handleElectronicClaim({
    claimType,
    claimUuid,
  }: {
    claimType: ClaimRequestVendorTypeEnum;
    claimUuid: string;
  }) {
    if (!claimUuid) {
      showSnackbar({
        message: "Claim ID is required to submit electronic claim",
        type: "error",
      });
      return;
    }
    const electronicClaimParams: ClaimRequest = {
      claimUuids: [claimUuid ?? ""],
      vendorType: claimType,
    };
    showElectronicClaimToastAndNavigate(
      addElectronicClaim({ claim: electronicClaimParams })
    );
  }
  const { mutateAsync: updateClaimStatus } = useUpdateClaimStatus();
  // Submitting a paper claim is a status change plus the printable CMS 1500
  // form; there is no clearinghouse involved.
  function handlePaperClaim(claimUuid: string) {
    if (!claimUuid) {
      showSnackbar({
        message: "Claim ID is required to submit paper claim",
        type: "error",
      });
      return;
    }
    updateClaimStatus({
      claimId: claimUuid,
      status: "SUBMITTED",
      xTENANTID: getXtenantId(),
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["Get-claim-count"] });
        viewPaperClaimDisc.open(claimUuid);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  const showElectronicClaimToastAndNavigate = (apiCall: Promise<any>) => {
    return apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({ queryKey: ["get-claims-list-v2"] });
        queryClient.invalidateQueries({ queryKey: ["Get-claim-count"] });
        navSelectClaimTypeDisc.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  // Ready for Submit is scoped to a single claim type: electronic by default,
  // paper while the layout's "Paper Claims" checkbox is on. An explicit claim
  // type picked in the filter drawer still wins.
  const claimTypeParam = filter?.claim_type
    ? (getValuesFromArrayOfObjects(filter?.claim_type, "value") as ClaimType[])
    : isReadyForSubmitView
      ? paperClaimFilter
        ? [CLAIM_TYPE.PaperClaim]
        : [CLAIM_TYPE.ElectronicClaim]
      : undefined;

  // The status counts are only claim-type scoped on the Ready for Submit card:
  // electronic by default, paper while the "Paper Claims" checkbox is on. Every
  // other card counts both claim types.
  const countClaimType = isReadyForSubmitView
    ? paperClaimFilter
      ? CLAIM_TYPE.PaperClaim
      : CLAIM_TYPE.ElectronicClaim
    : undefined;

  const { data: claimCount, refetch: refetchClaimCount } = useGetClaimCount({
    claimType: countClaimType,
    tenant: getXtenantId(),
  });

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  // Per-claim accordion. Only one claim is expanded at a time; the open claim's
  // ERA procedure breakdown is fetched by ClaimEraDetailsTable from the claim
  // uuid alone.
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);

  const toggleClaimExpand = (claimUuid: string) => {
    if (!claimUuid) return;
    setExpandedClaimId((prev) => (prev === claimUuid ? null : claimUuid));
  };

  // Denied-claim "View Denial": lazily fetch the lightweight claim payload
  // (`/api/master/claim/{claimUuid}/liteWeight`), which carries the denial JSON,
  // and surface it in the denial-details modal.
  const viewDenialDisc = useDisclosure();
  const [denialClaimId, setDenialClaimId] = useState<string | null>(null);
  const [denialRow, setDenialRow] = useState<any>(null);
  const { data: denialData, isFetching: denialLoading } = useGetClaimDenial(
    denialClaimId ?? "",
    !!denialClaimId
  );
  // "All time" in the range picker clears both dates while the dropdown still
  // reads Custom Range. Sending dateRange=CUSTOM_RANGE with no dates makes the
  // API return nothing, so an empty custom range has to drop dateRange too.
  const isCustomRange = filter?.date_range === "CUSTOM_RANGE";
  const hasCustomDates = Boolean(filter?.startDate || filter?.endDate);

  const {
    data: claimsList,
    isLoading: claimsDataLoading,
    isFetching: isClaimFetching,
    refetch,
  } = useGetClaimsV2({
    dateRange:
      isCustomRange && !hasCustomDates
        ? undefined
        : (filter?.date_range ?? undefined),
    // A custom range is inclusive of both days: send 00:00:00.000 for the
    // start and 23:59:59.999 for the end so a same-day range still returns
    // that day's claims.
    startDate:
      isCustomRange && filter?.startDate
        ? dayjs(filter?.startDate).startOf("day").toISOString()
        : undefined,
    endDate:
      isCustomRange && filter?.endDate
        ? dayjs(filter?.endDate).endOf("day").toISOString()
        : undefined,
    postingStatuses:
      selectedCard === "PAID" ? "PAID,PARTIALLY_PAID" : undefined,
    claimStatuses: filter?.claim_statuses
      ? getCommaSeparatedValues(filter?.claim_statuses, "value")
      : selectedCard && selectedCard !== "ALL"
        ? selectedCard === "PAID"
          ? undefined
          : selectedCard
        : undefined,
    patientUuids: filter?.patients
      ? getValuesFromArrayOfObjects(filter?.patients, "value")
      : undefined,
    providerUuids: filter?.providers
      ? getValuesFromArrayOfObjects(filter?.providers, "value")
      : undefined,
    // scrubStatus: filter?.scrubStatus
    //   ? getCommaSeparatedValues(filter?.scrubStatus, "value")
    //   : undefined,
    claimId: filter?.claimId ? filter.claimId.trim() : undefined,
    claimType: claimTypeParam,
    gridSorting: gridSorting,
    pagination,
  });

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  }, [filter?.claim_statuses]);

  const { hasView: hasViewClaims, hasUpdate: hasUpdateClaims } =
    useRoleController("Generate Claims");
  const { hasUpdate: hasUpdateClaimStatus } = useRoleController(
    "Change claim status"
  );
  const { hasUpdate: hasUpdateWaystarResponse } = useRoleController(
    "Claim status request responce"
  );

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        // Expand/collapse control. The detail row carries the claim's ERA
        // procedure breakdown and spans the full width (see colSpan below).
        field: "__expand",
        headerName: "",
        width: 44,
        sortable: false,
        disableColumnMenu: true,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          if (row?.__detail) {
            return (
              <Box sx={{ width: "100%" }}>
                <ClaimEraDetailsTable claimUuid={row?.parentUuid} />
              </Box>
            );
          }
          if (row?.__cptChild) return null;
          const isExpanded = expandedClaimId === row?.uuid;
          return (
            <IconButton
              size="small"
              onClick={() => toggleClaimExpand(row?.uuid)}
            >
              {isExpanded ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: "uuid",
        flex: 0.4,
        minWidth: 60,
        disableColumnMenu: true,
        headerName: "Claim ID",
        renderCell: ({ row }) => {
          if (row?.__detail) return null;
          // ECRH-5389 — child rows show the CPT code in place of the Claim ID.
          if (row?.__cptChild) {
            return (
              <Text isTabletext sx={{ pl: 1 }}>{`CPT ${row?.cptCode}`}</Text>
            );
          }
          return (
            <Text isTabletext>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
          );
        },
      },
      {
        field: "claimType",
        flex: 0.45,
        minWidth: 80,
        disableColumnMenu: true,
        headerName: "Claim Type",
        renderCell: ({ value }) => (value ? formatClaimType(value) : "-"),
      },
      {
        field: "patientName",
        flex: 0.7,
        minWidth: 120,
        disableColumnMenu: true,
        headerName: "Patient Name",
        renderCell: ({ row }) => (
          <PatientName
            patientId={row?.patientId ?? ""}
            name={row?.patientName ?? ""}
            gender={row?.patientGender ?? ""}
            birthDate={row?.patientDOB ?? ""}
          />
        ),
      },
      {
        field: "dateOfService",
        flex: 0.45,
        minWidth: 85,
        disableColumnMenu: true,
        headerName: "Visit ID",
        renderCell: ({ row }) => (
          <Box>
            <Text isTabletext>
              {row?.encounterId
                ? row?.encounterId?.split("-")?.[0]?.toUpperCase()
                : "-"}
            </Text>
            <Text isTabletext sx={{ color: "#667085", lineHeight: 1.2 }}>
              {NonNullableDate(row?.dateOfService)}
            </Text>
          </Box>
        ),
      },
      {
        field: "posCode",
        flex: 0.25,
        minWidth: 50,
        disableColumnMenu: true,
        headerName: "POS",
        sortable: false,
        renderCell: ({ row }) => {
          const desc = row?.posCode
            ? PLACES_OF_SERVICE_MAPPER?.[row.posCode]
            : "-";

          return <Tooltip title={desc}>{row?.posCode}</Tooltip>;
        },
      },
      {
        field: "providerName",
        flex: 0.4,
        minWidth: 80,
        disableColumnMenu: true,
        headerName: "Provider",
        renderCell: ({ row }) => {
          return (
            <Flex gap={"10px"} sx={{}}>
              <Box>
                <Text isTabletext marginBottom={"1px"}>
                  {" "}
                  {row?.providerName ?? ""}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      {
        field: "payerName",
        flex: 0.5,
        minWidth: 90,
        disableColumnMenu: true,
        headerName: "Payer",
        renderCell: ({ row }) => {
          return (
            <RenderWithTooltip value={row?.insurancePayer?.payerName ?? ""} />
          );
        },
      },
      {
        field: "insuranceType",
        flex: 0.3,
        minWidth: 60,
        disableColumnMenu: true,
        headerName: "Billed As",
      },
      // {
      //   field: "insuranceAmount",
      //   flex: 0.5,
      //   minWidth: 90,
      //   disableColumnMenu: true,
      //   headerName: "Insurance Amount",
      //   renderCell: () => "-",
      // },
      {
        field: "total",
        flex: 0.3,
        minWidth: 60,
        disableColumnMenu: true,
        headerName: "Claim Amount",
        renderCell: ({ row }) =>
          row?.total ? `$ ${toTwoDecimals(row?.total)}` : "-",
      },
      {
        field: "paidAmount",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Paid",
        renderCell: ({ row }) =>
          row?.paidAmount ? `$ ${toTwoDecimals(row?.paidAmount)}` : "-",
      },
      {
        field: "patientResponsibility",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Patient Responsibility",
        renderCell: ({ row }) =>
          row?.patientResponsibility
            ? `$ ${toTwoDecimals(row?.patientResponsibility)}`
            : "-",
      },
      {
        field: "adjustment",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Adjustment",
        renderCell: ({ row }) =>
          row?.adjustment ? `$ ${toTwoDecimals(row?.adjustment)}` : "-",
      },
      // Invoice column removed from the Claims table.
      // {
      //   field: "invoiceId",
      //   flex: 0.4,
      //   minWidth: 70,
      //   disableColumnMenu: true,
      //   headerName: "Invoice",
      //   sortable: false,
      //   renderCell: ({ row }) => {
      //     // Linked PR invoice (super bill) generated from this claim's PR balance.
      //     const linkedInvoiceUuid = row?.invoiceId;
      //     return linkedInvoiceUuid ? (
      //       <Text
      //         isTabletext
      //         color={"#004186"}
      //         sx={{ cursor: "pointer", textDecoration: "underline" }}
      //         onClick={() => setSelectedSuperBillUuid(linkedInvoiceUuid)}
      //       >
      //         {getFormattedId(linkedInvoiceUuid)}
      //       </Text>
      //     ) : (
      //       <Text isTabletext>-</Text>
      //     );
      //   },
      // },
      {
        field: "totalWriteOff",
        flex: 0.2,
        minWidth: 40,
        disableColumnMenu: true,
        headerName: "Write-Off",
        sortable: false,
        renderCell: ({ row }) =>
          row?.totalWriteOff ? `$ ${toTwoDecimals(row?.totalWriteOff)}` : "-",
      },
      {
        field: "active",
        flex: 0.45,
        minWidth: 110,
        disableColumnMenu: true,
        headerName: "Status",
        sortable: false,
        renderCell: ({ row }) => {
          if (!row?.claimStatus) return null;
          const statusLabel = capitalize(
            replaceUnderScoreAddSpace(row?.claimStatus)
          );
          return (
            <Tooltip title={statusLabel} placement="top">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <StatusChip
                  label={statusLabel}
                  variant={ClaimStatusVariantMap[row?.claimStatus] ?? "default"}
                />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "postingStatus",
        flex: 0.35,
        minWidth: 90,
        disableColumnMenu: true,
        headerName: "Posting Status",
        sortable: false,
        // Backend-derived ERA posting status (UNPAID / PARTIALLY_PAID / PAID),
        // shown only on the parent claim row. CPT child rows carry their own
        // posting status in the Status column.
        renderCell: ({ row }) => {
          if (row?.__cptChild || !row?.postingStatus) return null;
          const statusLabel = capitalize(
            replaceUnderScoreAddSpace(row?.postingStatus)
          );
          return (
            <Tooltip title={statusLabel} placement="top">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <StatusChip
                  label={statusLabel}
                  variant={
                    ClaimStatusVariantMap[row?.postingStatus] ?? "default"
                  }
                />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "Action",
        width: 40,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          if (row?.__cptChild) return null;
          return (
            <ActionMenu
              options={roleFilter({
                list: getClaimActionList(row),
                compWith: {
                  view_claim: hasViewClaims,
                  edit_claim: hasUpdateClaims,
                  submit_claim: hasUpdateClaims,
                  change_status: hasUpdateClaimStatus,
                  resubmit_claim: hasUpdateClaims,
                  waystar_response: hasUpdateWaystarResponse,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit_claim" || v === "resubmit_claim") {
                  navigate(
                    `edit-claim?patientId=${row?.patientId}&claimId=${row?.uuid}&claimStatus=UPDATED`
                  );
                } else if (v === "delete_claim") {
                  openDeleteModal(row.uuid);
                } else if (v === "view_claim") {
                  // Every claim type opens the read-only claim details view
                  // (Service/Provider/Diagnosis/CPT). The CMS 1500 form stays
                  // reachable through the separate "Print Claim" action.
                  navigate(
                    `edit-claim?patientId=${row?.patientId}&claimId=${row?.uuid}&readOnly=true`
                  );
                  // Paper claims used to open the CMS 1500 drawer here, which
                  // duplicated "Print Claim".
                  // if (isPaperClaim(row)) {
                  //   viewPaperClaimDisc.open(row?.uuid);
                  // }
                  // setSearchParams({
                  //   patientId: row?.patientId,
                  //   claimId: row?.uuid,
                  //   readOnly: "true",
                  // });
                } else if (v === "update_era_eob_details") {
                  queryClient.prefetchQuery({
                    queryKey: ["Get-claim-by-id", row?.uuid],
                    queryFn: async () =>
                      new ClaimControllerApi(getConfigObject())
                        .getClaimById(row?.uuid, getXtenantId())
                        .then((it) => it?.data),
                  });

                  navigate(
                    `${PROVIDER_PATHS.BILLING}update-era-eob?patientId=${row?.patientId}&claimId=${row?.uuid}`
                  );
                } else if (v === "create_invoice_for_pr") {
                  navigate(
                    `../create-pr-invoice/${row?.patientId}/${row?.uuid}`
                  );
                } else if (v === "submit_claim") {
                  // Paper claims never go to a clearinghouse — mark them
                  // submitted and print the CMS 1500 form instead.
                  if (isPaperClaim(row)) {
                    handlePaperClaim(row?.uuid ?? "");
                  } else if (singleVendor) {
                    handleElectronicClaim({
                      claimType: singleVendor,
                      claimUuid: row?.uuid ?? "",
                    });
                  } else {
                    navSelectClaimTypeDisc.open(row?.uuid);
                  }
                } else if (v === "waystar_response") {
                  waystarResponseDisc.open(row?.error);
                } else if (v === "view_denial") {
                  // Fetch the liteWeight payload for the denied claim and show
                  // the denial details in a modal. The row supplies Date of
                  // Service, which the liteWeight payload omits.
                  setDenialClaimId(row?.uuid);
                  setDenialRow(row);
                  viewDenialDisc.open();
                } else if (v === "write_off" || v === "appeal") {
                  // TODO: Denied-claim Write Off / Appeal flows are not yet
                  // implemented (no backend endpoint or dedicated UI exists).
                  showSnackbar({
                    message: "This action is not available yet.",
                    type: "info",
                  });
                } else if (v === "fax_claim") {
                  createFaxClaimDisc.open({
                    ...DOCUMENT_INITIAL_VALUE,
                    sCPFromName: row.providerName,
                    documentDescription: "Fax Claim",
                  });
                  setSearchParams(
                    {
                      patientId: row?.patientId,
                      claimId: row?.uuid,
                    },
                    { replace: true }
                  );
                } else if (v === "change_status") {
                  changeClaimStatusDisclosureHost.open(row);
                } else if (v === "send_email") {
                  sendMailDisclosure.open([
                    {
                      uuid: row?.patientId,
                      name: row?.patientName,
                      claimUuid: row?.uuid,
                    },
                  ]);
                } else if (v === "print_on_cms_form") {
                  // Open drawer with claim UUID, will fetch data in ViewPaperClaimDrawer
                  viewPaperClaimDisc.open(row?.uuid);
                }
              }}
            />
          );
        },
      },
    ],
    ["dateOfService", "patientName", "total"]
  );

  // The detail row spans every column so the ERA procedure table renders full
  // width.
  COLUMNS[0] = {
    ...COLUMNS[0],
    colSpan: (params) => (params?.row?.__detail ? COLUMNS.length : undefined),
  };

  // Collapse duplicate claim records into a single accordion parent (keeps grid
  // row ids unique) and inject a full-width detail row under the expanded claim,
  // which renders that claim's ERA procedure breakdown.
  const seenClaimIds = new Set<string>();
  const claimRows = (claimsList?.data?.content ?? []).filter((claim) => {
    const id = claim?.uuid;
    if (!id) return true;
    if (seenClaimIds.has(id)) return false;
    seenClaimIds.add(id);
    return true;
  });
  const displayRows = claimRows.flatMap((claim) =>
    claim?.uuid && claim.uuid === expandedClaimId
      ? [
          claim,
          {
            id: `${claim.uuid}__detail`,
            __detail: true,
            parentUuid: claim.uuid,
          },
        ]
      : [claim]
  );

  function archiveHandler<T>(_value: boolean, id: T) {
    return archieveAPI({
      claimId: id?.toString() as any,
      status: true,
    }).then((res) => {
      refetch();
      refetchClaimCount();
      return res;
    });
  }
  return (
    <PageLayout
      header={
        <>
          {/* <CreateFaxClaimDrawer
            disClosureHost={createFaxClaimDisc}
            title="Send Fax Claim"
          /> */}
          <UploadFaxDocumentDrawer
            disclosureHost={createFaxClaimDisc}
            allowSecondDocument={true}
          />
          {changeClaimStatusDisclosureHost.opened && (
            <ChangeClaimStatus
              open={changeClaimStatusDisclosureHost.opened}
              onClose={changeClaimStatusDisclosureHost.close}
              disclosureHost={changeClaimStatusDisclosureHost}
            />
          )}
          {sendMailDisclosure.opened && (
            <SendBulkEmailModal
              disableAddPatient
              // Email Claim is addressed to the claim's patient, so the To
              // recipient is fixed.
              lockRecipients
              allowAttachment
              disClosureHost={sendMailDisclosure}
              onSave={() => {
                refetch();
              }}
            />
          )}

          <DeleteModal
            w="380px"
            open={openedDeleteModal}
            onClose={closeDeleteModal}
            id={deleteId}
            onConfirmation={(id) => {
              return archiveHandler(true, id);
            }}
            content={"You want to delete this Claim"}
          />
          <ClearingHouseResponseModal
            response={waystarResponseDisc.data}
            open={waystarResponseDisc.opened}
            onClose={waystarResponseDisc.close}
          />
          {viewDenialDisc.opened && (
            <ViewDenialModal
              open={viewDenialDisc.opened}
              onClose={() => {
                viewDenialDisc.close();
                setDenialClaimId(null);
                setDenialRow(null);
              }}
              data={denialData as any}
              loading={denialLoading}
              dateOfService={denialRow?.dateOfService}
            />
          )}
          <NavSelectClaimTypeModal
            disclosureHost={navSelectClaimTypeDisc}
            onNextClick={(v) => {
              if (v === "WAYSTAR") {
                handleElectronicClaim({
                  claimType: ClaimRequestVendorTypeEnum.Waystar,
                  claimUuid: navSelectClaimTypeDisc.data ?? "",
                });
              } else if (v === "CLAIM_MD") {
                handleElectronicClaim({
                  claimType: ClaimRequestVendorTypeEnum.ClaimMd,
                  claimUuid: navSelectClaimTypeDisc.data ?? "",
                });
              } else if (v === "STEDI") {
                handleElectronicClaim({
                  claimType: ClaimRequestVendorTypeEnum.Stedi,
                  claimUuid: navSelectClaimTypeDisc.data ?? "",
                });
              }
            }}
          />
          <ViewPaperClaimDrawer disclosureHost={viewPaperClaimDisc} />
          {/* <ActiveFilters filters={filter} setFilters={setClaimFilter as any} /> */}
          <Box display="flex" alignItems="center">
            <Box flex={1} display="flex" alignItems="center">
              <ClaimStatusCards
                counts={claimCount?.data ?? {}}
                onCardClick={(statusValue) => {
                  setSelectedCard(statusValue);
                }}
                activeIndex={(() => {
                  if (!selectedCard) return null;
                  if (selectedCard === "ALL") return 0;
                  const idx = CLAIMS_STATUS_LIST.findIndex(
                    (s) => s.value === selectedCard
                  );
                  return idx === -1 ? null : idx + 1;
                })()}
              />
            </Box>
            {/* {selectedCard && (
              <Box display="flex" alignItems="center" height="100%" pb={3}>
                <IconButton
                  aria-label="Clear selection"
                  onClick={() => {
                    setSelectedCard(undefined);
                    refetchClaimCount();
                  }}
                  sx={{ ml: 1 }}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            )} */}
          </Box>
        </>
      }
    >
      {selectedSuperBillUuid && (
        <PrintSuperBill
          key={selectedSuperBillUuid}
          superBillUuid={selectedSuperBillUuid}
          patientName={
            claimsList?.data?.content?.find(
              (item) => item?.invoiceId === selectedSuperBillUuid
            )?.patientName
          }
          onClose={() => {
            setSelectedSuperBillUuid(null);
          }}
        />
      )}
      <CustomDataGridV2
        searchString={searchKey}
        loading={claimsDataLoading || isClaimFetching}
        rows={displayRows}
        getRowId={(row) =>
          row?.__detail || row?.__cptChild ? row?.id : row?.uuid
        }
        getRowHeight={(params) => (params?.model?.__detail ? "auto" : null)}
        isRowSelectable={(params) =>
          !params?.row?.__detail &&
          !params?.row?.__cptChild &&
          // ECRH-5479 — paper claims cannot participate in Batch Submission, so
          // their row checkbox stays disabled (and is excluded from select-all).
          !isPaperClaim(params?.row)
        }
        columns={COLUMNS}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        paginationModel={pagination}
        rowSelectionModel={rowSelectionModel}
        keepNonExistentRowsSelected
        onRowSelectionModelChange={(rowSelected) => {
          // `keepNonExistentRowsSelected` persists selections across pages, but
          // the header "Deselect All" only clears the current page. A row
          // checkbox can only ever remove one row at a time, so dropping more
          // than one in a single change means the header cleared the page —
          // reset the whole selection so it clears across every page at once.
          const removedCount = rowSelectionModel.length - rowSelected.length;
          setRowSelectionModel(removedCount > 1 ? [] : rowSelected);
        }}
        rowCount={claimsList?.data.page.totalElements}
        checkboxSelection={isBulkClaimActionAllowed && !paperClaimFilter}
        rowSelection
        sortModel={gridSorting}
        rowHeight={BILLING_TABLE_ROW_HEIGHT}
      />
    </PageLayout>
  );
};

export default ClaimsList;

// Paper claims come back as either "paper_claim" or "paper".
function isPaperClaim(row: any): boolean {
  const claimType = row?.claimType?.toLowerCase();
  return claimType === "paper_claim" || claimType === "paper";
}

type ClaimActionOption = {
  value: string;
  label: string;
  isDisabled?: boolean;
  hoverMsg?: string;
};

// Per-status row action menus. Each claim status exposes only the actions
// relevant to its stage in the claim lifecycle; the default branch covers the
// remaining statuses (Denied/Rejected/Paid) with the full action set.
const getBaseClaimActionList = (row: any): ClaimActionOption[] => {
  const isElectronic = row?.claimType === "electronic_claim";
  const updateStatusAction: ClaimActionOption = {
    value: "change_status",
    label: "Update Status",
    isDisabled: isElectronic,
    hoverMsg: "You cannot change the status of an electronic claim",
  };

  // Update ERA/EOB removed from the claim action menu. Kept for reference.
  // Once every CPT on the claim has been posted the backend marks the claim as
  // fully paid; ERA/EOB details can no longer be edited, so the action stayed
  // visible but disabled. Unpaid / partially-paid claims remained editable.
  // const isPostingPaid = row?.postingStatus === "PAID";
  // const updateEraEobAction: ClaimActionOption = {
  //   value: "update_era_eob_details",
  //   label: "Update ERA/EOB",
  //   isDisabled: isPostingPaid,
  //   hoverMsg: isPostingPaid
  //     ? "This claim is fully paid; ERA/EOB details can no longer be updated"
  //     : undefined,
  // };

  switch (row?.claimStatus) {
    case "DRAFT":
      return [
        { value: "view_claim", label: "View Claim" },
        { value: "edit_claim", label: "Edit Claim" },
      ];
    case "READY_FOR_SUBMIT":
      return [
        { value: "view_claim", label: "View Claim" },
        { value: "edit_claim", label: "Edit Claim" },
        { value: "submit_claim", label: "Submit" },
      ];
    case "SUBMITTED":
      return [
        { value: "view_claim", label: "View Claim" },
        { value: "print_on_cms_form", label: "Print Claim" },
        updateStatusAction,
        { value: "send_email", label: "Email Claim" },
        { value: "fax_claim", label: "Fax Claim" },
      ];
    case "ACCEPTED":
      return [
        { value: "view_claim", label: "View Claim" },
        { value: "print_on_cms_form", label: "Print Claim" },
        updateStatusAction,
        // ERA/EOB updates only applied to paper claims.
        // ...(!isElectronic ? [updateEraEobAction] : []),
        { value: "send_email", label: "Email Claim" },
        { value: "fax_claim", label: "Fax Claim" },
      ];
    case "PAID":
      return [
        { value: "view_claim", label: "View Claim" },
        { value: "print_on_cms_form", label: "Print Claim" },
        // ERA/EOB updates (partially-paid / payment posting) only applied to
        // paper claims.
        // ...(!isElectronic ? [updateEraEobAction] : []),
        { value: "send_email", label: "Email Claim" },
        { value: "fax_claim", label: "Fax Claim" },
      ];
    case "DENIED":
      return [
        {
          value: "view_denial",
          label: "View Denial",
          hoverMsg:
            row?.error === null
              ? "Denial details are not available"
              : undefined,
        },
        // Resubmit is not fully built for denied claims, so the action stays
        // visible but disabled until the flow is specified.
        {
          value: "resubmit_claim",
          label: "Resubmit",
          isDisabled: true,
          hoverMsg: "Resubmitting a denied claim is not available yet",
        },
        // { value: "write_off", label: "Write Off" },
        // { value: "appeal", label: "Appeal" },
      ];
    default:
      return [
        { value: "view_claim", label: "View Claim" },
        {
          value: "edit_claim",
          label: "Edit Claim",
          isDisabled:
            row?.claimStatus !== "DRAFT" && row?.claimStatus !== "REJECTED",
          hoverMsg: "You can only edit a draft or rejected claim",
        },
        updateStatusAction,
        { value: "fax_claim", label: "Fax Claim" },
        // ...(!isElectronic
        //   ? [{ ...updateEraEobAction, label: "Update ERA/EOB Details" }]
        //   : []),
        {
          value: "resubmit_claim",
          label: "Resubmit Claim",
          isDisabled: !["DENIED", "REJECTED"].includes(row?.claimStatus),
          hoverMsg: "You can only resubmit a denied or rejected claim",
        },
        {
          value: "print_on_cms_form",
          label: "Print Claim Data on CMS 1500 Form",
        },
        {
          value: "waystar_response",
          label: "Clearing House Response",
          isDisabled: row?.error === null,
          hoverMsg:
            row?.error === null ? "Claim response is not available" : undefined,
        },
        { value: "send_email", label: "Send Email" },
      ];
  }
};

// "Create Invoice for PR" is shown solely based on the claim carrying a patient
// responsibility amount — no claim-status gating. Appended to the status action
// list only when that amount is present.
const getClaimActionList = (row: any): ClaimActionOption[] => {
  const baseActions = getBaseClaimActionList(row);
  const hasPatientResponsibility = Number(row?.patientResponsibility) > 0;
  // Same reason Resubmit is disabled above: the PR invoice flow off a denied
  // claim is unspecified, so it is shown disabled on denied claims.
  const isDenied = row?.claimStatus === "DENIED";
  // A claim can carry only one PR invoice; the listing marks the ones already
  // generated so a duplicate cannot be created.
  const isPrInvoiceGenerated = row?.superBillGenerated === true;
  // Only part of the claim has been posted from the ERA/EOB, so the patient
  // responsibility can still move and must not be invoiced yet.
  const isPartiallyPosted =
    row?.postingStatus === CLAIM_POSTING_STATUS_PARTIALLY_PAID;
  return hasPatientResponsibility
    ? [
        ...baseActions,
        {
          value: "create_invoice_for_pr",
          label: "Create Invoice for PR",
          isDisabled: isDenied || isPrInvoiceGenerated || isPartiallyPosted,
          hoverMsg: isPrInvoiceGenerated
            ? "A PR invoice has already been generated for this claim"
            : isDenied
              ? "Creating a PR invoice from a denied claim is not available yet"
              : isPartiallyPosted
                ? CLAIM_NOT_COMPLETELY_POSTED_MSG
                : undefined,
        },
      ]
    : baseActions;
};

const ClaimStatusVariantMap: Record<
  string,
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending"
> = {
  ACCEPTED: "success",
  DRAFT: "warning",
  REJECTED: "error",
  SUBMITTED: "info",
  DENIED: "error",
  PASSED: "success",
  PENDING: "pending",
  UNPAID: "neutral",
  PARTIALLY_PAID: "warning",
  PAID: "success",
};
